// Owner-side enrollment hand-off. Mirrors the staff /staff/enroll flow:
// owner picks a branch (owners aren't tied to one), optionally prefills
// fields, POSTs to mint an enrollment token, and shows the resulting QR
// for the customer to scan with their own phone. The customer-facing
// /e/:branch_slug page handles consent + submit — same as the staff flow.

import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import {
  ALL_ONBOARDING_FIELDS,
  ONBOARDING_FIELD_LABELS,
  type MembershipConfig,
  type OnboardingFieldKey,
} from '@naya/shared'

import { ownerApi, OwnerApiError } from '../ownerApi.js'
import { useBranches } from '../hooks/useBranches.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import { WalletInstallNudge } from '../../shared/WalletInstallNudge.js'
import '../../staff/pages/staff.css'

const LAST_BRANCH_KEY = 'naya:lastUsedBranchId'

interface MintResponse {
  // token + expires_at are null when no prefill data was captured — the
  // server hands back the plain public enrollment URL instead, which never
  // expires (see the enrollment-tokens route).
  token: string | null
  expires_at: string | null
  enroll_url: string
  branch_slug: string
}


export function OwnerEnrollPage() {
  const branches = useBranches()
  const branchList = (branches.data?.branches ?? []).filter(
    (b) => !b.archived_at,
  )

  const [branchId, setBranchId] = useState<string | null>(null)
  useEffect(() => {
    if (branchId || branchList.length === 0) return
    const remembered =
      typeof window !== 'undefined'
        ? localStorage.getItem(LAST_BRANCH_KEY)
        : null
    const found = remembered && branchList.some((b) => b.id === remembered)
    setBranchId(found ? remembered : branchList[0]!.id)
  }, [branchId, branchList])

  useEffect(() => {
    if (branchId) localStorage.setItem(LAST_BRANCH_KEY, branchId)
  }, [branchId])

  const [values, setValues] = useState<
    Partial<Record<OnboardingFieldKey, string>>
  >({})
  const [minted, setMinted] = useState<MintResponse | null>(null)

  // Which program to enroll into. Loyalty AND membership programs are issuable
  // here — the mint endpoint accepts a program_id for either. The picker is
  // surfaced whenever the tenant runs more than one active program; otherwise
  // the mint uses the tenant default. (SLICE-MEMBERSHIP-PASS — issuing a
  // membership is the gated hand-off: take payment, then hand off this QR.)
  const programsQ = usePrograms()
  const activePrograms = (programsQ.data?.programs ?? []).filter(
    (p) =>
      p.status === 'active' &&
      (p.pass_type === 'loyalty' || p.pass_type === 'membership'),
  )
  const multiProgram = activePrograms.length > 1
  const defaultProgram =
    activePrograms.find((p) => p.is_default) ?? activePrograms[0]
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null)
  const effectiveProgramId = selectedProgramId ?? defaultProgram?.id ?? null

  // The program whose onboarding shape drives the form. Re-derived from the
  // SELECTED program so switching the picker updates which inputs render (e.g.
  // a VIP program that requires phone). Falls back to the default.
  const selectedProgram =
    activePrograms.find((p) => p.id === effectiveProgramId) ?? defaultProgram
  const onboarding = selectedProgram?.onboarding_fields
  const selectedIsMembership = selectedProgram?.pass_type === 'membership'

  // SLICE-MEMBERSHIP-PASS — tier options for a tiered membership. The owner
  // picks the member's tier here; it rides on the minted token to card-create.
  const enrollMembershipCfg = selectedIsMembership
    ? (selectedProgram?.type_config as MembershipConfig | null)
    : null
  const tierOptions: string[] = enrollMembershipCfg?.tiers_enabled
    ? enrollMembershipCfg.tiers.map((t) => t.trim()).filter(Boolean)
    : []
  const needsTier = tierOptions.length > 0
  const [selectedTier, setSelectedTier] = useState<string>('')
  // Reset the tier whenever the selected program changes.
  useEffect(() => {
    setSelectedTier('')
  }, [effectiveProgramId])

  const enabledKeys = useMemo<OnboardingFieldKey[]>(() => {
    if (!onboarding) return ['first_name', 'email']
    return ALL_ONBOARDING_FIELDS.filter((k) => onboarding[k].enabled)
  }, [onboarding])

  // Drop captured values for fields the newly-selected program doesn't collect,
  // so a now-hidden field's stale value isn't carried into the mint.
  useEffect(() => {
    setValues((prev) => {
      const next: Partial<Record<OnboardingFieldKey, string>> = {}
      for (const k of enabledKeys) {
        if (prev[k] !== undefined) next[k] = prev[k]!
      }
      return next
    })
  }, [enabledKeys])

  const mint = useMutation({
    mutationFn: async () => {
      if (!branchId) throw new Error('Select a branch first')
      const prefilled_fields: Record<string, string> = {}
      for (const [k, v] of Object.entries(values)) {
        const trimmed = (v ?? '').trim()
        if (trimmed.length > 0) prefilled_fields[k] = trimmed
      }
      return ownerApi.post<MintResponse>('/staff/enrollment-tokens', {
        prefilled_fields,
        branch_id: branchId,
        // Only send when there's a real choice; a single-program tenant omits
        // it so the minted URL stays the bare branch URL.
        ...(multiProgram && effectiveProgramId
          ? { program_id: effectiveProgramId }
          : {}),
        // The chosen tier for a tiered membership (validated server-side).
        ...(needsTier && selectedTier
          ? { membership_tier: selectedTier }
          : {}),
      })
    },
    onSuccess: (res) => setMinted(res),
    onError: (err) => {
      const message =
        err instanceof OwnerApiError ? err.message : 'Could not create QR'
      toast.error(message)
    },
  })

  const expiresInMin = useMemo(() => {
    if (!minted?.expires_at) return null
    const diff = new Date(minted.expires_at).getTime() - Date.now()
    return Math.max(0, Math.round(diff / 60_000))
  }, [minted])

  function reset() {
    setMinted(null)
    setValues({})
  }

  if (programsQ.isLoading) {
    return (
      <div className="staff-enroll">
        <p className="stamp-hint">Loading enrollment form…</p>
      </div>
    )
  }
  if (programsQ.isError) {
    return (
      <div className="staff-enroll">
        <h1>Enrollment unavailable</h1>
        <p className="stamp-error">
          {programsQ.error instanceof OwnerApiError
            ? programsQ.error.message
            : 'Could not load the loyalty program.'}
        </p>
      </div>
    )
  }

  if (minted) {
    return (
      <div className="staff-enroll-result">
        <h1>Show this QR to the customer</h1>
        <p className="stamp-hint">
          They scan with their own phone to confirm and add the pass to their
          wallet.
        </p>
        <div className="staff-enroll-qr">
          <QRCodeSVG
            value={minted.enroll_url}
            size={280}
            level="M"
            includeMargin
          />
        </div>
        <p className="staff-enroll-meta">
          {minted.token === null
            ? "Reusable link. It doesn't expire."
            : `Expires in ${expiresInMin} min${expiresInMin === 1 ? '' : 's'}`}
        </p>
        <WalletInstallNudge />
        <div className="staff-enroll-actions">
          <button
            type="button"
            className="owner-btn"
            onClick={() => {
              if (navigator.clipboard?.writeText) {
                void navigator.clipboard
                  .writeText(minted.enroll_url)
                  .then(() => toast.success('Link copied'))
                  .catch(() => toast.error('Copy failed'))
              }
            }}
          >
            Copy link
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={reset}
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  const multiBranch = branchList.length > 1

  return (
    <div className="staff-enroll">
      <h1>New customer</h1>
      <p className="stamp-hint">
        Type whatever the customer gives you — anything you skip, they'll fill
        in themselves after scanning the QR.
      </p>

      {multiBranch && (
        <div className="staff-enroll-field">
          <label htmlFor="owner-enroll-branch" className="stamp-label">
            Branch
          </label>
          <select
            id="owner-enroll-branch"
            className="owner-input"
            value={branchId ?? ''}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {branchList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {multiProgram && (
        <div className="staff-enroll-field">
          <label htmlFor="owner-enroll-program" className="stamp-label">
            Program
          </label>
          <select
            id="owner-enroll-program"
            className="owner-input"
            value={effectiveProgramId ?? ''}
            onChange={(e) => setSelectedProgramId(e.target.value)}
          >
            {activePrograms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.pass_type === 'membership' ? ' (membership)' : ''}
                {p.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedIsMembership && (
        <p className="stamp-hint" style={{ textAlign: 'left' }}>
          Issuing a membership — collect payment first, then hand off this QR
          for the customer to add their membership pass.
        </p>
      )}

      {needsTier && (
        <div className="staff-enroll-field">
          <label htmlFor="owner-enroll-tier" className="stamp-label">
            Tier <span className="staff-enroll-req"> · required</span>
          </label>
          <select
            id="owner-enroll-tier"
            className="owner-input"
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
          >
            <option value="">Select a tier…</option>
            {tierOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      )}

      <form
        className="stamp-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!mint.isPending) mint.mutate()
        }}
      >
        {enabledKeys.map((key) => (
          <EnrollField
            key={key}
            field={key}
            required={!!onboarding?.[key]?.required}
            value={values[key] ?? ''}
            onChange={(v) => setValues((prev) => ({ ...prev, [key]: v }))}
          />
        ))}
        <button
          type="submit"
          className="owner-btn owner-btn-primary owner-btn-block"
          disabled={mint.isPending || !branchId || (needsTier && !selectedTier)}
        >
          {mint.isPending ? 'Creating…' : 'Generate QR'}
        </button>
        {needsTier && !selectedTier && (
          <p className="stamp-hint staff-enroll-skip-note">
            Pick the member's tier to generate the QR.
          </p>
        )}
        <p className="stamp-hint staff-enroll-skip-note">
          You can hit Generate without filling anything — the customer can do
          it all on their phone.
        </p>
      </form>

      {/* Available before generating the enrollment QR — if the customer
          has no Google Wallet app, the staff/owner can sort that out first
          rather than minting an enrollment QR that has nowhere to land. */}
      <WalletInstallNudge />
    </div>
  )
}

function EnrollField({
  field,
  required,
  value,
  onChange,
}: {
  field: OnboardingFieldKey
  required: boolean
  value: string
  onChange: (v: string) => void
}) {
  const label = ONBOARDING_FIELD_LABELS[field]
  const id = `owner-enroll-${field}`
  const placeholder = PLACEHOLDER[field]
  const inputType =
    field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'

  if (field === 'birthday') {
    return (
      <BirthdayField
        id={id}
        value={value}
        onChange={onChange}
        note={required ? 'Required for the customer' : 'Optional'}
      />
    )
  }

  return (
    <div className="staff-enroll-field">
      <label htmlFor={id} className="stamp-label">
        {label}
        {required && <span className="staff-enroll-req"> · required</span>}
      </label>
      <input
        id={id}
        className="owner-input"
        type={inputType}
        autoComplete="off"
        autoCapitalize={field === 'email' ? 'none' : 'words'}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

const PLACEHOLDER: Record<OnboardingFieldKey, string> = {
  first_name: 'e.g. Maria',
  last_name: 'e.g. Cruz',
  email: 'e.g. maria@example.com',
  phone: 'e.g. 09171234567',
  birthday: '',
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

function BirthdayField({
  id,
  value,
  onChange,
  note,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  note: string
}) {
  const [m, d] = value.split('-')
  const month = m ?? ''
  const day = d ?? ''

  function update(nextMonth: string, nextDay: string) {
    if (!nextMonth && !nextDay) {
      onChange('')
      return
    }
    onChange(`${nextMonth}-${nextDay}`)
  }

  return (
    <div className="staff-enroll-field">
      <label htmlFor={`${id}-month`} className="stamp-label">
        Birthday <span className="staff-enroll-req"> · {note}</span>
      </label>
      <div className="staff-birthday-row">
        <select
          id={`${id}-month`}
          className="owner-input"
          value={month}
          onChange={(e) => update(e.target.value, day)}
        >
          <option value="">Month</option>
          {MONTHS.map((name, i) => {
            const mm = String(i + 1).padStart(2, '0')
            return (
              <option key={mm} value={mm}>
                {name}
              </option>
            )
          })}
        </select>
        <select
          id={`${id}-day`}
          className="owner-input"
          value={day}
          onChange={(e) => update(month, e.target.value)}
        >
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => {
            const dd = String(i + 1).padStart(2, '0')
            return (
              <option key={dd} value={dd}>
                {i + 1}
              </option>
            )
          })}
        </select>
      </div>
    </div>
  )
}
